import React from "react";
import { log } from '@/lib/utils/logger';
import { generateCTAKey } from '@/lib/utils/keyGenerator';
import { ComponentRendererProps } from "@/store/sagas/types/deviceUnlock";
import InputRenderer from "./InputRenderer";

// Type for CTA objects
interface CTAObject {
  action?: string;
  text?: string;
  style?: string;
}

/**
 * Get CSS class for status item based on its ID
 */
const getStatusItemClassName = (itemId: string): string => {
  switch (itemId) {
    case 'device_identifier':
      return 'font-mono text-gray-800';
    case 'lock_status':
      return 'text-green-600';
    default:
      return 'text-gray-600';
  }
};

/**
 * Get button styling based on CTA style
 */
const getButtonClassName = (ctaStyle?: string): string => {
  return ctaStyle === 'primary' 
    ? 'bg-black text-white hover:bg-gray-800' 
    : 'bg-gray-200 text-gray-800 hover:bg-gray-300';
};

/**
 * Check if button should be a submit type
 */
const isSubmitButton = (cta: CTAObject): boolean => {
  return cta.action === 'native' && (cta.text === 'Submit' || cta.text === 'Submit Unlock Request');
};

/**
 * Check if button should handle click event
 */
const shouldHandleClick = (cta: CTAObject): boolean => {
  return cta.action === 'native' && !isSubmitButton(cta);
};

/**
 * Get status color based on device state
 */
const getStatusColor = (isIneligible: boolean, isUnlocked: boolean): string => {
  if (isIneligible) return 'text-red-600';
  if (isUnlocked) return 'text-green-600';
  return 'text-gray-800';
};

/**
 * Get status class name for different status items
 */
const getDetailedStatusClassName = (itemId: string, isIneligible: boolean, isUnlocked: boolean): string => {
  if (itemId === 'device_identifier') {
    return 'font-mono text-gray-800 block mt-1';
  }
  if (itemId === 'lock_status') {
    const statusColor = getStatusColor(isIneligible, isUnlocked);
    return `block mt-1 ${statusColor}`;
  }
  return 'text-gray-600 block mt-1';
};

// Status Display Component Renderer (for locked/unlocked/ineligible/eligible devices)
const StatusDisplayRenderer: React.FC<ComponentRendererProps> = ({ 
  component, 
  onSubmit, 
  onNavigate
}) => {
  const isUnlocked = component.display === 'UNLOCKED';
  const isIneligible = component.display === 'INELIGIBLE';
  const isForm = component.display === 'FORM';
  const isConfirmation = component.display === 'CONFIRMATION';

  // Handle form submission for eligible devices
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());
    log.debug('ComponentRenderer form submission:', data);
    if (onSubmit) {
      onSubmit(data);
    }
  };

  // Handle CTA button clicks
  const handleCTAClick = (ctaId: string) => {
    if (ctaId === 'back_to_status_check' && onNavigate) {
      onNavigate('');
    }
  };
  
  return (
    <div className="w-full min-h-screen bg-white text-gray-800 font-sans flex flex-col items-center pt-8">
      <div className="w-full max-w-2xl px-6 md:px-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-10 text-center">{component.details.title}</h1>
        <div className={`border p-6 md:p-8 rounded-xl shadow-lg w-full max-w-xl mx-auto ${
          isConfirmation ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
        }`}>
          {isConfirmation ? (
            <div className="space-y-6">
              {/* Success Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              
              {component.details.content.section.map((section) => (
                <div key={section.id}>
                  {section.id === 'confirmation_message' && (
                    <div className="text-center mb-8">
                      <p className="text-green-800 font-medium text-lg">{section.description || section.text}</p>
                    </div>
                  )}
                  
                  {section.id === 'order_summary_card' && (
                    <div className="bg-white border border-green-200 rounded-lg p-6 mb-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">{section.title}</h3>
                      {section.content?.section && (
                        <div className="space-y-3">
                          {section.content.section.map((item) => (
                            <div key={item.id} className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
                              <span className="font-medium text-gray-700">{item.title}:</span>
                              <span className="text-gray-900 font-mono text-sm">{item.description || item.text}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {section.id === 'unlock_instructions_section' && (
                    <div className="bg-white border border-green-200 rounded-lg p-6 mb-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{section.title}</h3>
                      <p className="text-gray-700 mb-6">{section.description || section.text}</p>
                      
                      {section.content?.section && (
                        <div className="space-y-6">
                          {section.content.section.map((step) => (
                            <div key={step.id} className="border-l-4 border-green-400 pl-4">
                              <h4 className="font-semibold text-gray-900 mb-2">{step.title}</h4>
                              {step.description && (
                                <div className="text-gray-700 text-sm whitespace-pre-line">{step.description}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {section.footer && (
                        <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-400">
                          <p className="text-blue-800 text-sm">{section.footer.body}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {section.id === 'back_action' && section.cta && (
                    <div className="text-center">
                      <button
                        onClick={() => onNavigate && onNavigate('')}
                        className="bg-black text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                      >
                        {section.cta.text}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : isForm ? (
            <form onSubmit={handleFormSubmit}>
              {component.details.content.section.map((section) => (
                <div key={section.id} className="mb-6">
                  {section.id === 'status_summary_card' && section.content && (
                    <div className="space-y-4 mb-8">
                      {section.content.section?.map((statusItem) => (
                        <div key={statusItem.id} className="text-base">
                          <span className="font-bold">{statusItem.title}</span>
                          <span className={`block mt-1 ${getStatusItemClassName(statusItem.id)}`}>
                            {statusItem.description || statusItem.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {section.id === 'unlock_request_form' && section.content && (
                    <div className="space-y-4">
                      {section.content.section?.map((formItem) => (
                        <div key={formItem.id}>
                          {formItem.input && <InputRenderer input={formItem.input} />}
                          {formItem.description && (
                            <p className="text-xs text-gray-600 mb-4">{formItem.description}</p>
                          )}
                        </div>
                      ))}
                      
                      {/* Render CTAs if available */}
                      {section.ctas && (
                        <div className="flex space-x-4 mt-8">
                          {section.ctas.map((cta, index) => {
                            const buttonClassName = getButtonClassName(cta.style);
                            const isSubmit = isSubmitButton(cta);
                            const shouldClick = shouldHandleClick(cta);
                            
                            return (
                              <button
                                key={generateCTAKey(cta, index)}
                                type={isSubmit ? 'submit' : 'button'}
                                onClick={() => shouldClick ? handleCTAClick(cta.text) : undefined}
                                className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${buttonClassName}`}
                              >
                                {cta.text}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </form>
          ) : (
            <>
              {component.details.content.section.map((section) => (
                <div key={section.id} className="mb-6">
                  {section.id === 'status_summary_card' && section.content && (
                    <div className="space-y-4">
                      {section.content.section?.map((statusItem) => (
                        <div key={statusItem.id} className="text-base">
                          <span className="font-bold">{statusItem.title}</span>
                          <span className={getDetailedStatusClassName(statusItem.id, isIneligible, isUnlocked)}>
                            {statusItem.description || statusItem.text}
                          </span>
                        </div>
                      ))}
                      {section.cta && (
                        <button
                          onClick={() => onNavigate && onNavigate('')}
                          className="mt-8 w-full bg-black text-white py-3 rounded-lg font-bold text-lg hover:bg-gray-800 transition duration-150 ease-in-out shadow-lg"
                        >
                          {section.cta.text}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatusDisplayRenderer;
